import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import Google from '../../../../assets/img/google-app.svg';
import Apple from '../../../../assets/img/apple-app.svg';

const STORAGE_KEY = 'deprecation_modal_last_closed';
const DEV_DISABLE_STORAGE_KEY = 'ednipro_popup_dev_disabled';
const COOLDOWN_MS_FALLBACK = 10 * 60 * 1000; // 10 minutes
const HIDE_AFTER_FALLBACK = '2026-07-01T00:00:00';
const EDNIPRO_IOS_FALLBACK = 'https://shrt.ednipro.dp.ua/zTn22IvLR';
const EDNIPRO_ANDROID_FALLBACK = 'https://shrt.ednipro.dp.ua/u2toAsbB-';

const DeprecationModal = ({
  cityId,
  edniproPopup,
  edniproUrls,
  edniproPopupText,
  edniproPopupTitle,
  edniproCloseAviableTo,
  edniproCooldown,
  onVisibilityChange,
}) => {
  const [visible, setVisible] = useState(false);
  const prevCityIdRef = useRef(cityId);
  const [isDevDisabled, setIsDevDisabled] = useState(() => localStorage.getItem(DEV_DISABLE_STORAGE_KEY) === 'true');

  useEffect(() => {
    if (onVisibilityChange) onVisibilityChange(visible);
  }, [visible, onVisibilityChange]);

  const hideAfterDate = new Date(edniproCloseAviableTo || HIDE_AFTER_FALLBACK);
  const isHideAfterDateValid = !Number.isNaN(hideAfterDate.getTime());
  const hideAfter = isHideAfterDateValid ? hideAfterDate : new Date(HIDE_AFTER_FALLBACK);
  const cooldownMs = Number.isFinite(+edniproCooldown) && +edniproCooldown >= 0 ? +edniproCooldown : COOLDOWN_MS_FALLBACK;

  const isPastDeadline = new Date() >= hideAfter;

  useEffect(() => {
    if (visible && isPastDeadline) {
      document.body.classList.add('ednipro-modal-blocking');
    } else {
      document.body.classList.remove('ednipro-modal-blocking');
    }
    return () => {
      document.body.classList.remove('ednipro-modal-blocking');
    };
  }, [visible, isPastDeadline]);

  useEffect(() => {
    if (prevCityIdRef.current !== cityId) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
      prevCityIdRef.current = cityId;
    }
  }, [cityId]);

  useEffect(() => {
    const updateDevDisabled = () => {
      setIsDevDisabled(localStorage.getItem(DEV_DISABLE_STORAGE_KEY) === 'true');
    };

    const handleStorage = (event) => {
      if (event.key === DEV_DISABLE_STORAGE_KEY) updateDevDisabled();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('edniproPopupDevToggle', updateDevDisabled);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('edniproPopupDevToggle', updateDevDisabled);
    };
  }, []);

  useEffect(() => {
    if (isDevDisabled) {
      setVisible(false);
      return;
    }

    const isApp = !!window.ReactNativeWebView;
    const isPopupEnabledForPlatform = isApp ? !!edniproPopup?.app : !!edniproPopup?.web;

    if (!isPopupEnabledForPlatform) {
      setVisible(false);
      return;
    }

    // Only for Dnipro city (city_id = 1)
    if (cityId !== 1) {
      setVisible(false);
      return;
    }

    // After deprecation date — always show, no cooldown
    if (new Date() >= hideAfter) {
      setVisible(true);
      return;
    }

    const lastClosed = localStorage.getItem(STORAGE_KEY);
    if (lastClosed) {
      const elapsed = Date.now() - parseInt(lastClosed, 10);
      if (elapsed < cooldownMs) return;
    }

    setVisible(true);
  }, [cityId, cooldownMs, edniproPopup, hideAfter, isDevDisabled]);

  if (!visible) return null;

  const handleClose = () => {
    try { localStorage.setItem(STORAGE_KEY, Date.now().toString()); } catch {}
    setVisible(false);
  };

  const platform = window.mobilePlatform || '';
  const isIOS = platform.toLowerCase() === 'ios';

  const iosLink = edniproUrls?.ios || EDNIPRO_IOS_FALLBACK;
  const androidLink = edniproUrls?.android || EDNIPRO_ANDROID_FALLBACK;
  const storeLink = isIOS ? iosLink : androidLink;
  const storeImg = isIOS ? Apple : Google;
  const storeAlt = isIOS ? 'App Store' : 'Google Play';
  const popupTextHtml =
    edniproPopupText ||
    "З <strong>01.07.2026</strong> запис на прийом до лікарів закладів міста Дніпро можливий через міський додаток <strong>«єДніпро»</strong>:";
  const popupTitle = edniproPopupTitle || 'Шановні користувачі!';

  return (
    <div className="deprecation-overlay" role="dialog" aria-modal="true" aria-labelledby="deprecation-title">
      <div className="deprecation-modal">
        {!isPastDeadline && (
          <button className="deprecation-modal__close" onClick={handleClose} aria-label="Закрити">
            <i className="fas fa-times" />
          </button>
        )}

        <div className="deprecation-modal__body">
          
          <p className="deprecation-modal__greeting">{popupTitle}</p>
          <p className="deprecation-modal__text" dangerouslySetInnerHTML={{ __html: popupTextHtml }} />

          <ul className="apps-list deprecation-modal__apps">
            <li className="apps-list__item">
              <a href={storeLink} className="apps-list__link" rel="noopener noreferrer" target="_blank">
                <div className="deprecation-modal__store-wrapper">
                  <div className="deprecation-modal__logo">
                    <svg width="140" viewBox="0 0 398 116" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M69.747 25.1948C58.5151 25.1948 51.4058 28.5541 44.5489 31.8294C38.2388 34.8108 32.2653 37.6662 22.9265 37.6662C15.733 37.6662 10.6009 36.0285 5.0901 33.551C3.87016 33.0051 2.69228 32.4592 1.47234 31.8714C1.17787 31.7034 0.84134 31.5774 0.546871 31.4515V69.1176C0.546871 104.852 44.002 115.77 44.002 115.77C44.002 115.77 87.4992 104.81 87.4992 69.0756V28.4701C82.6195 26.5805 77.0246 25.1948 69.747 25.1948ZM76.6039 69.0756C76.6039 79.8674 71.0931 89.0635 60.1978 96.4539C53.9298 100.695 47.4936 103.215 44.002 104.39C35.7569 101.661 11.4422 91.5829 11.4422 69.0756V48.6259C14.8496 49.6757 18.5095 50.3055 22.9265 50.3055C32.2653 50.3055 38.2388 47.4921 44.5489 44.4688C51.4479 41.1934 58.5572 37.8341 69.747 37.8341C72.2289 37.8341 74.5005 38.0021 76.6039 38.296V69.0756Z" fill="#1565C0"/>
                      <path d="M29.3627 18.8541V23.3471H33.8218V18.8541H29.3627Z" fill="#1565C0"/>
                      <path d="M0 9.32206V16.5446H7.23551V9.32206H0Z" fill="#1565C0"/>
                      <path d="M20.3604 13.4792V18.9381H25.8291V13.4792H20.3604Z" fill="#1565C0"/>
                      <path d="M10.3064 16.9645V23.7251H17.0371V16.9645H10.3064Z" fill="#1565C0"/>
                      <path d="M35.7148 10.8757C35.7148 10.8757 35.7569 10.6238 35.7148 10.4558V10.8757Z" fill="#1565C0"/>
                      <path d="M76.6039 0.461903C74.4585 0.167964 72.1869 0 69.7049 0C58.473 0 51.3637 3.3593 44.5068 6.63462C41.5621 8.02033 38.9119 9.36405 35.7569 10.4138V11.3376H32.9805V14.9489H35.7569V20.6597H40.1739V17.6783H42.2773V19.19H45.264V17.7623H46.9046V18.2242H48.0404V17.7623C53.9298 15.1169 60.4081 12.7653 69.7049 12.7653C72.1869 12.7653 74.5005 12.9333 76.6039 13.2272C80.7264 13.7731 84.2601 14.8229 87.4992 16.0827V3.31731C84.2601 2.05757 80.7264 1.04978 76.6039 0.461903Z" fill="#1565C0"/>
                      <path d="M129.575 64.1479C129.575 52.0775 141.064 42.3604 158.336 42.3604V50.9388C149.586 50.9388 142.738 54.3549 140.379 59.7449H158.336L156.89 67.2604H139.847C140.988 71.208 144.412 74.3964 150.955 74.3964C154.075 74.3964 156.129 74.1686 158.336 73.5613V81.4565C156.129 82.4434 153.694 82.8229 150.194 82.8229C137.031 82.8229 129.575 74.3964 129.575 64.1479Z" fill="#1565C0"/>
                      <path d="M189.652 41.3735L175.957 72.6504H194.598V41.3735H189.652ZM170.478 41.3735V32.4156H205.859L204.641 41.3735V72.6504H211.641L207.533 92.2363H197.565L199.772 81.6083H170.783V92.2363H160.816V72.6504H166.142L179.381 41.3735H170.478Z" fill="#1565C0"/>
                      <path d="M215.286 81.6083V43.6509H225.253V57.7711H239.329V43.6509L249.296 42.6641V81.6083H239.329V66.2735H225.253V81.6083H215.286Z" fill="#1565C0"/>
                      <path d="M260.167 37.5778L261.232 24.9L271.732 26.1906L269.982 36.5909L260.167 37.5778ZM254.232 51.6979V44.4101L269.754 42.5122V81.6083H259.787V51.6979H254.232Z" fill="#1565C0"/>
                      <path d="M277.329 81.6083V43.6509H300.611L310.578 42.6641V81.6083H300.611V52.1534H287.296V81.6083H277.329Z" fill="#1565C0"/>
                      <path d="M334.173 51.1665C331.663 51.1665 329.532 52.0016 328.163 52.9125V74.3964C337.749 73.7891 343.228 67.7159 343.228 60.504C343.228 54.279 338.586 51.1665 334.173 51.1665ZM318.195 92.6159V43.2714H321.695L324.586 45.6247C327.021 44.0305 330.749 42.4363 335.239 42.4363C345.586 42.4363 353.195 49.2686 353.195 60.504C353.195 72.8022 342.543 82.1397 328.163 82.747V92.6159H318.195Z" fill="#1565C0"/>
                      <path d="M377.701 51.2424C370.701 51.2424 367.354 55.9491 367.354 63.3888C367.354 68.9305 370.473 74.0168 376.94 74.0168C383.864 74.0168 387.212 69.3101 387.212 61.8705C387.212 56.3287 384.092 51.2424 377.701 51.2424ZM357.462 63.9961C357.462 52.0775 364.843 42.4363 377.701 42.4363C390.484 42.4363 397.103 51.5461 397.103 61.2631C397.103 73.1818 389.799 82.8229 376.94 82.8229C364.082 82.8229 357.462 73.7132 357.462 63.9961Z" fill="#1565C0"/>
                    </svg>
                  </div>
                </div>
              </a>
            </li>
          </ul>

          <div className="deprecation-modal__also-block">
            <p className="deprecation-modal__also">Запис також можливий:</p>
            <ul className="deprecation-modal__also-list">
              <li>
                <i className="fas fa-hospital" />
                в ході візиту в реєстратуру
              </li>
              <li>
                <i className="fas fa-phone" />
                за телефоном реєстратури
              </li>
              <li>
                <i className="fas fa-globe" />
                <a href={window.location.origin} target="_blank" rel="noopener noreferrer" className="deprecation-modal__site-link">
                  <span>через медичний кабінет</span>
                  <span>Web-сайт Likari24/7 <i className="fas fa-external-link-alt" /></span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <style>{`
        .deprecation-overlay {
          position: absolute;
          inset: 0;
          background: #edf4f8d9;
          z-index: 10;
          display: flex;
          align-items: baseline;
          justify-content: center;
          padding: 16px;
          border-radius: 8px;
        }
        .deprecation-modal {
          background: #fff;
          border-radius: 16px;
          padding: 15px 20px 20px;
          max-width: 360px;
          width: 100%;
          position: relative;
          box-shadow: 0 8px 32px rgba(0,0,0,0.18);
        }
        .deprecation-modal__close {
          position: absolute;
          top: 12px;
          right: 12px;
          background: none;
          border: none;
          font-size: 18px;
          color: #666;
          cursor: pointer;
          padding: 4px 8px;
          line-height: 1;
        }
        .deprecation-modal__close:hover {
          color: #333;
        }
        .deprecation-modal__logo {
          display: flex;
          justify-content: center;
          padding: 5px;
          height: 50px;
          background: #e8f1fd;
          border-radius: 12px 12px 0 0;
          border-bottom: none;
        }
        .deprecation-modal__store-wrapper {
          display: inline-flex;
          flex-direction: column;
          align-items: stretch;
          border-radius: 12px;
          overflow: hidden;
          border: 2px solid #c5d8f5;
        }
        .deprecation-modal__open-btn {
          display: block;
          background: #e8f1fd;
          color: #1565C0;
          font-size: 11px;
          font-weight: 500;
          text-align: center;
          padding: 4px 12px;
          letter-spacing: 0.2px;
          opacity: 0.8;
        }
        .apps-list__link:hover .deprecation-modal__open-btn {
          background: #d0e4f7;
          opacity: 1;
        }
        .deprecation-modal__body {
          text-align: center;
        }
        .deprecation-modal__greeting {
          font-size: 17px;
          font-weight: 700;
          margin-bottom: 5px;
          color: #222;
        }
        .deprecation-modal__text {
          font-size: 14px;
          line-height: 1.6;
          color: #444;
          margin-bottom: 20px;
          text-align: left;
        }
        .deprecation-modal__apps {
          justify-content: center;
          margin-top: 0;
          margin-bottom: 16px;
        }
        .deprecation-modal__thanks {
          font-size: 14px;
          color: #666;
          margin-bottom: 10px;
        }
        .deprecation-modal__also-block {
          background: #f0f6ff;
          border-radius: 10px;
          padding: 10px 12px;
          text-align: left;
        }
        .deprecation-modal__also {
          font-size: 12px;
          font-weight: 600;
          color: #1565C0;
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }
        .deprecation-modal__also-list {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .deprecation-modal__also-list li {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #444;
          padding: 4px 0;
          border-bottom: 1px solid #ddeaf9;
        }
        .deprecation-modal__also-list li:last-child {
          border-bottom: none;
        }
        .deprecation-modal__also-list .fas {
          width: 16px;
          text-align: center;
          color: #1565C0;
          font-size: 13px;
          flex-shrink: 0;
        }
        .deprecation-modal__site-link {
          color: #1565C0;
          text-decoration: none;
          font-weight: 500;
          display: inline-flex;
          flex-direction: column;
          line-height: 1.4;
        }
        .deprecation-modal__site-link span:first-child {
          font-size: 12px;
          color: #555;
          font-weight: 400;
        }
        .deprecation-modal__site-link span:last-child {
          font-size: 13px;
          color: #1565C0;
          font-weight: 600;
          text-decoration: underline;
        }
        .deprecation-modal__site-link:hover span:last-child {
          color: #0d47a1;
        }
      `}</style>
    </div>
  );
};

const mapStateToProps = ({ user }) => ({
  cityId: user?.city?.id,
  edniproPopup: user?.edniproPopup,
  edniproUrls: user?.edniproUrls,
  edniproPopupText: user?.edniproPopupText,
  edniproPopupTitle: user?.edniproPopupTitle,
  edniproCloseAviableTo: user?.edniproCloseAviableTo,
  edniproCooldown: user?.edniproCooldown,
});

export default connect(mapStateToProps)(DeprecationModal);
